"use client";

import { api } from "@agroproj/backend/convex/_generated/api";
import { useAuth, useClerk } from "@clerk/nextjs";
import { useQuery } from "convex/react";
import { Building2, Check, ChevronsUpDown, Plus } from "lucide-react";
import type { Route } from "next";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useSidebar } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { buildOrganizationPath, extractOrganizationSlug, getRelativeOrganizationPath } from "@/lib/organization-path";
import { cn } from "@/lib/utils";
export function OrganizationSwitcher() {
  const router = useRouter();
  const pathname = usePathname();
  const {
    isLoaded: isAuthLoaded,
    isSignedIn,
    orgId,
    orgSlug,
    sessionId
  } = useAuth();
  const {
    setActive
  } = useClerk();
  const {
    state,
    isMobile
  } = useSidebar();
  const isCollapsed = state === "collapsed" && !isMobile;
  const organizations = useQuery(api.check_membership.listUserOrganizations, isAuthLoaded && isSignedIn ? {} : "skip");
  const [isLoading, setIsLoading] = useState(false);

  // Auto-select first organization if none selected
  useEffect(() => {
    if (isAuthLoaded && isSignedIn && sessionId && organizations && !orgId && organizations.length > 0) {
      const firstOrg = organizations[0];
      if (firstOrg) {
        setActive({
          organization: firstOrg.clerkOrganizationId
        }).catch(error => {
          console.error("Failed to auto-select organization", error);
        });
      }
    }
  }, [isAuthLoaded, isSignedIn, organizations, orgId, sessionId, setActive]);
  if (organizations === undefined) {
    return <div aria-busy="true" className="flex h-8 w-full items-center gap-2 rounded-md px-2.5 group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:px-0">
        <Skeleton className="size-5 shrink-0 rounded-full" />
        <Skeleton className="h-4 flex-1 group-data-[collapsible=icon]:hidden" />
      </div>;
  }
  const activeOrganization = organizations.find(organization => organization.clerkOrganizationId === orgId || organization.organizationSlug === orgSlug || organization.isActive);
  const handleSwitch = async (clerkOrganizationId: string, organizationSlug: string) => {
    if (!isAuthLoaded || !isSignedIn || !sessionId) {
      return;
    }
    setIsLoading(true);
    try {
      // Set active organization in Clerk
      await setActive({
        organization: clerkOrganizationId
      });

      // Set active organization in Convex backend
      // This needs the organization's Convex ID, which we'll need to fetch
      // For now, we'll just navigate to the new organization

      // Get current relative path within organization
      const currentOrgSlug = extractOrganizationSlug(pathname);
      const relativePath = currentOrgSlug ? getRelativeOrganizationPath(currentOrgSlug, pathname) : "/projects";

      // Navigate to same path in new organization context
      const newPath = buildOrganizationPath(organizationSlug, relativePath);
      router.push(newPath as Route);
    } catch (error_0) {
      console.error("Failed to switch organization:", error_0);
    } finally {
      setIsLoading(false);
    }
  };
  const currentOrgName = activeOrganization?.organizationName ?? "Selecionar organização";
  const currentOrgImage = activeOrganization?.organizationImageUrl;
  const trigger = <DropdownMenuTrigger render={<Button className={cn("text-muted-foreground hover:bg-muted/50 hover:text-foreground w-full group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:overflow-visible group-data-[collapsible=icon]:p-0!", isCollapsed ? "justify-center gap-0" : "justify-between gap-2")} size="sm" variant="ghost" />}>
      <div className={cn("flex items-center gap-2 truncate", isCollapsed ? "w-full justify-center gap-0" : "justify-start")}>
        <Avatar className="size-5">
          <AvatarImage alt={currentOrgName} src={currentOrgImage} />
          <AvatarFallback className="bg-muted text-muted-foreground">
            <Building2 className="size-3" />
          </AvatarFallback>
        </Avatar>
        <span className={cn("truncate font-medium", isCollapsed && "sr-only")}>
          {currentOrgName}
        </span>
      </div>
      <ChevronsUpDown className={cn("size-4 shrink-0 opacity-50", isCollapsed && "hidden")} />
    </DropdownMenuTrigger>;
  return <DropdownMenu>
      {isCollapsed && !isMobile ? <Tooltip>
          <TooltipTrigger render={trigger} />
          <TooltipContent side="right">{currentOrgName}</TooltipContent>
        </Tooltip> : trigger}
      <DropdownMenuContent align="start" className="w-60">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Organizações</DropdownMenuLabel>

          {/* Organizations list */}
          {organizations.map(organization_0 => {
          const isActive = orgId === organization_0.clerkOrganizationId || orgSlug === organization_0.organizationSlug || organization_0.isActive;
          return <DropdownMenuItem className="gap-2" disabled={isLoading || isActive} key={organization_0.organizationId} onClick={() => handleSwitch(organization_0.clerkOrganizationId, organization_0.organizationSlug)}>
                <Avatar className="size-5">
                  <AvatarImage alt={organization_0.organizationName} src={organization_0.organizationImageUrl} />
                  <AvatarFallback>
                    {/* ui-audit-ignore-next-line button/no-icon-size-override: glyph sized to the avatar, not the menu row */}
                    <Building2 className="size-3" />
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-1 flex-col truncate">
                  <span className="truncate">{organization_0.organizationName}</span>
                  {organization_0.role && <span className="text-muted-foreground">
                      {organization_0.role === "admin" ? "Admin" : organization_0.role === "owner" ? "Proprietário" : "Membro"}
                    </span>}
                </div>
                {isActive && <Check />}
              </DropdownMenuItem>;
        })}
        </DropdownMenuGroup>

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => router.push("/onboarding/choose-organization")}>
          <Plus />
          Criar organização
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>;
}